import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, MapPin, Clock, AlertCircle, Package, CreditCard } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function Shipping() {
  return (
    <>
      <SEOHead
        title="Shipping & Delivery Policy - RRIDE Moto Studio"
        description="Read the shipping and delivery policy of RRIDE Moto Studio. Understand how we deliver motorcycle accessories and gear across India."
        canonical="/shipping"
        pathname="/shipping"
      />
      <main className="min-h-screen bg-gradient-to-br from-[#102318] via-[#183326] to-[#0b1710] text-white pt-20 px-4 pb-16 font-[Cormorant_Garamond]">
      <section className="max-w-5xl mx-auto">
        <p className="text-[#ffcc32] font-black uppercase text-sm tracking-widest">Policies</p>
        <h1 className="text-5xl md:text-7xl uppercase mt-2 mb-8" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
          Shipping & Delivery
        </h1>
        
        <div className="bg-black/30 border border-white/10 rounded-lg p-8 mb-8">
          <p className="text-zinc-400 leading-relaxed">
            We strive to deliver your orders as quickly and safely as possible. Please read our shipping and delivery policy to understand how we process and ship your orders.
          </p>
        </div>

        <div className="space-y-8 text-zinc-300">
          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Shipping Coverage</h2>
            </div>
            <p className="leading-relaxed">
              We currently ship <span className="text-white font-semibold">only within India</span>. We do not offer international shipping at this time. We deliver to most pin codes across India, except certain remote areas where courier services may not be available.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Truck className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Shipping Partners</h2>
            </div>
            <p className="leading-relaxed">
              Shipping is handled through <span className="text-white font-semibold">Shiprocket</span> and their network of trusted courier partners. We use multiple courier services to ensure timely delivery across different regions in India.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Order Processing Time</h2>
            </div>
            <p className="leading-relaxed">
              Orders are processed within <span className="text-white font-semibold">1-2 business days</span> after confirmation. Business days exclude Sundays and public holidays. You will receive a confirmation email once your order is processed and shipped.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Package className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Estimated Delivery Time</h2>
            </div>
            <p className="leading-relaxed mb-4">
              Estimated delivery time is <span className="text-white font-semibold">3-7 business days</span> depending on the destination. Delivery times may vary based on:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your location (metro cities typically receive faster delivery)</li>
              <li>Product availability</li>
              <li>Courier service coverage in your area</li>
              <li>External factors (weather, strikes, etc.)</li>
            </ul>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Express Delivery</h2>
            </div>
            <p className="leading-relaxed">
              <span className="text-white font-semibold">Express Delivery is currently not available.</span> We only offer standard shipping through our courier partners. We are working on adding express delivery options in the future.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Shipping Charges</h2>
            </div>
            <p className="leading-relaxed">
              Shipping charges are <span className="text-white font-semibold">calculated during checkout</span> based on your location, order weight, and delivery address. The shipping cost will be displayed before you complete your payment. Some orders may qualify for free shipping based on order value or promotional offers.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Delivery Delays</h2>
            <p className="leading-relaxed mb-4">
              While we strive to deliver on time, delivery delays may occur due to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Courier company operational issues</li>
              <li>Adverse weather conditions</li>
              <li>Strikes or local unrest</li>
              <li>Remote or hard-to-reach locations</li>
              <li>Address verification issues</li>
            </ul>
            <div className="bg-[#ffcc32]/10 border border-[#ffcc32]/20 rounded-lg p-4 mt-4">
              <p className="text-sm text-zinc-300">
                <span className="text-[#ffcc32] font-semibold">Important:</span> Delivery delays due to courier companies, weather, strikes, or other unforeseen circumstances are beyond our control. We cannot be held responsible for such delays.
              </p>
            </div>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Order Tracking</h2>
            <p className="leading-relaxed">
              Once your order is shipped, you will receive a tracking number via email and SMS. You can track your order status through the courier's website or app using the provided tracking number. You can also track your order from our website's "Track Order" section.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Delivery Instructions</h2>
            <p className="leading-relaxed mb-4">
              To ensure smooth delivery, please:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide complete and accurate shipping address</li>
              <li>Include correct pin code and contact number</li>
              <li>Ensure someone is available at the delivery address to receive the package</li>
              <li>Check the package condition before signing for delivery</li>
            </ul>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Failed Delivery Attempts</h2>
            <p className="leading-relaxed">
              If the courier is unable to deliver the package due to incorrect address, recipient unavailability, or other reasons, the package will be returned to our warehouse. In such cases, you may need to pay additional shipping charges for re-delivery. If you wish to cancel the order after failed delivery, our standard refund policy will apply.
            </p>
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#ffcc32]/10 to-transparent border border-[#ffcc32]/20 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white uppercase mb-3">Shipping Questions?</h3>
          <p className="text-zinc-400 mb-4">
            If you have any questions about shipping or need help with delivery issues, please contact us:
          </p>
          <div className="space-y-2 text-sm">
            <p><span className="text-[#ffcc32]">Email:</span> rridemods@gmail.com</p>
            <p><span className="text-[#ffcc32]">Phone:</span> +91 8793678780</p>
            <p><span className="text-[#ffcc32]">WhatsApp:</span> +91 8793678780</p>
          </div>
        </div>

        <div className="mt-8 text-center text-zinc-500 text-sm">
          <p>Last Updated: July 2026</p>
        </div>
      </section>
    </main>
    </>
  );
}